var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import * as React from 'react';
import { Box } from '@mui/material';
import { MultiRecordFormTabs, useDynamicFormOverrides, } from '../../../components/FormComponents/dynamicForm';
import { fetchEquipmentTypeOptions } from '../asset_lookup_service';
import { ASSET_ACTION, ASSET_FIELD, buildAssetSections, buildAssetValueDerivation, CREATE_APPLICATION_STEP_ID, } from '../config/create_application_schema';
import ConfirmationAndInformationPoup, { POPUP_TYPE, } from '../../../components/UtilityComponents/ConfirmationAndInformationPoup';
import VinLookupDialog from './VinLookupDialog';
var getAssetTabLabel = function (recordIndex) { return "Asset ".concat(recordIndex + 1); };
var AssetInformationStep = React.forwardRef(function (_a, ref) {
    var onAddNewLocation = _a.onAddNewLocation, locationOptions = _a.locationOptions, programAspireOId = _a.programAspireOId, vendorConfig = _a.vendorConfig, isPrimaryCustomerVeteran = _a.isPrimaryCustomerVeteran, primaryCustomerState = _a.primaryCustomerState;
    var recordsRef = React.useRef(null);
    var _b = React.useState([]), equipmentTypeOptions = _b[0], setEquipmentTypeOptions = _b[1];
    var _c = React.useState(false), isVinDialogOpen = _c[0], setIsVinDialogOpen = _c[1];
    var removeConfirmResolveRef = React.useRef(null);
    var _d = React.useState(false), isRemoveConfirmationOpen = _d[0], setIsRemoveConfirmationOpen = _d[1];
    var confirmRemoveAsset = React.useCallback(function () {
        var _a;
        (_a = removeConfirmResolveRef.current) === null || _a === void 0 ? void 0 : _a.call(removeConfirmResolveRef, false);
        return new Promise(function (resolve) {
            removeConfirmResolveRef.current = resolve;
            setIsRemoveConfirmationOpen(true);
        });
    }, []);
    var resolveRemoveConfirmation = React.useCallback(function (confirmed) {
        var _a;
        (_a = removeConfirmResolveRef.current) === null || _a === void 0 ? void 0 : _a.call(removeConfirmResolveRef, confirmed);
        removeConfirmResolveRef.current = null;
        setIsRemoveConfirmationOpen(false);
    }, []);
    React.useEffect(function () {
        var isCancelled = false;
        fetchEquipmentTypeOptions(programAspireOId).then(function (options) {
            if (!isCancelled) {
                setEquipmentTypeOptions(options);
            }
        });
        return function () {
            isCancelled = true;
        };
    }, [programAspireOId]);
    var deriveAssetValues = React.useMemo(function () { return buildAssetValueDerivation(equipmentTypeOptions); }, [equipmentTypeOptions]);
    var resolveSections = useDynamicFormOverrides().resolveSections;
    var getSections = React.useCallback(function () {
        var schemaSections = buildAssetSections(equipmentTypeOptions, (vendorConfig === null || vendorConfig === void 0 ? void 0 : vendorConfig.IsVinDecoderEnabled) !== 0, locationOptions, isPrimaryCustomerVeteran, primaryCustomerState);
        return resolveSections
            ? resolveSections(CREATE_APPLICATION_STEP_ID.AssetInformation, schemaSections)
            : schemaSections;
    }, [
        equipmentTypeOptions,
        isPrimaryCustomerVeteran,
        locationOptions,
        primaryCustomerState,
        resolveSections,
        vendorConfig,
    ]);
    var openVinLookup = React.useCallback(function () { return setIsVinDialogOpen(true); }, []);
    var closeVinLookup = React.useCallback(function () { return setIsVinDialogOpen(false); }, []);
    var toKnownManufacturer = React.useCallback(function (manufacturer, equipmentTypeCode) {
        var _a, _b, _c;
        var manufacturers = (_b = (_a = equipmentTypeOptions.find(function (option) { return option.value === equipmentTypeCode; })) === null || _a === void 0 ? void 0 : _a.manufacturers) !== null && _b !== void 0 ? _b : [];
        var matched = manufacturers.find(function (candidate) { return candidate.name.toLowerCase() === manufacturer.toLowerCase(); });
        return (_c = matched === null || matched === void 0 ? void 0 : matched.name) !== null && _c !== void 0 ? _c : manufacturer;
    }, [equipmentTypeOptions]);
    var applyVinValues = React.useCallback(function (decodedValues) {
        var _a;
        var _b, _c, _d, _e, _f, _g, _h, _j;
        var activeIndex = (_c = (_b = recordsRef.current) === null || _b === void 0 ? void 0 : _b.getActiveRecordIndex()) !== null && _c !== void 0 ? _c : 0;
        var activeRecord = (_f = ((_e = (_d = recordsRef.current) === null || _d === void 0 ? void 0 : _d.getRecords()) !== null && _e !== void 0 ? _e : [])[activeIndex]) !== null && _f !== void 0 ? _f : {};
        var manufacturer = String((_g = decodedValues[ASSET_FIELD.Manufacturer]) !== null && _g !== void 0 ? _g : '');
        (_h = recordsRef.current) === null || _h === void 0 ? void 0 : _h.mergeActiveRecord(__assign(__assign({}, decodedValues), (manufacturer
            ? (_a = {},
                _a[ASSET_FIELD.Manufacturer] = toKnownManufacturer(manufacturer, String((_j = activeRecord[ASSET_FIELD.EquipmentType]) !== null && _j !== void 0 ? _j : '')),
                _a) : {})));
    }, [toKnownManufacturer]);
    var actionHandlers = React.useMemo(function () {
        var _a;
        return (_a = {},
            _a[ASSET_ACTION.AddNewLocation] = function () { return onAddNewLocation(); },
            _a[ASSET_ACTION.VinLookup] = openVinLookup,
            _a);
    }, [onAddNewLocation, openVinLookup]);
    React.useImperativeHandle(ref, function () { return ({
        validate: function () { var _a, _b; return (_b = (_a = recordsRef.current) === null || _a === void 0 ? void 0 : _a.validate()) !== null && _b !== void 0 ? _b : true; },
        getPayload: function () { var _a, _b; return ({ assets: (_b = (_a = recordsRef.current) === null || _a === void 0 ? void 0 : _a.getRecords()) !== null && _b !== void 0 ? _b : [] }); },
        getFiles: function () { var _a, _b; return (_b = (_a = recordsRef.current) === null || _a === void 0 ? void 0 : _a.getFiles()) !== null && _b !== void 0 ? _b : {}; },
        reset: function () { var _a; return (_a = recordsRef.current) === null || _a === void 0 ? void 0 : _a.reset(); },
        getPreview: function () {
            var _a, _b;
            return ({
                label: 'Asset Information',
                records: ((_b = (_a = recordsRef.current) === null || _a === void 0 ? void 0 : _a.getRecords()) !== null && _b !== void 0 ? _b : []).map(function (values, index) { return ({
                    label: getAssetTabLabel(index),
                    sections: getSections(),
                    values: values,
                }); }),
            });
        },
    }); }, [getSections]);
    return (React.createElement(Box, null,
        React.createElement(MultiRecordFormTabs, { ref: recordsRef, idPrefix: "asset", getSections: getSections, getRecordLabel: getAssetTabLabel, addLabel: "Add Asset", maxRecords: (vendorConfig === null || vendorConfig === void 0 ? void 0 : vendorConfig.IsMultipleAssetEnabled) === 1 ? undefined : 1, actionHandlers: actionHandlers, deriveValues: deriveAssetValues, confirmRemoveRecord: confirmRemoveAsset }),
        React.createElement(VinLookupDialog, { open: isVinDialogOpen, onApply: applyVinValues, onClose: closeVinLookup }),
        React.createElement(ConfirmationAndInformationPoup, { popupType: POPUP_TYPE.DELETE_CONFIRMATION_WARNING, open: isRemoveConfirmationOpen, titleText: "Are you sure?", message: "This asset will be removed from the application.", acceptBtnText: "Delete", acceptCallBack: function () { return resolveRemoveConfirmation(true); }, denyBtnText: "Cancel", denyCallBack: function () { return resolveRemoveConfirmation(false); }, cancelCallBack: function () { return resolveRemoveConfirmation(false); } })));
});
AssetInformationStep.displayName = 'AssetInformationStep';
export default AssetInformationStep;
